import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@deere/fuel-react';
import DownloadIcon from '@deere/ux.brand-foundations/icons/download.svg';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { visuallyHidden } from '@mui/utils';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox.jsx';
import styles from './Page.module.css';
import { getComparator } from './sortingUtil.js';
import { LoggedInContext } from '../../context/LoggedInContext.jsx';
import { errorBannerFromResponse } from '../../utils/errorBanner';

const columns = [
    {
        id: 'bidNumber',
        label: 'Job #',
    },
    { id: 'facility', label: 'Facility' },
    {
        id: 'department',
        label: 'Department',
    },
    {
        id: 'classification',
        label: 'Classification',
    },
    { id: 'noOfOpenings', label: '# Openings' },
    { id: 'reqNumber', label: 'Req #' },
    { id: 'jobGrade', label: 'Job Grade' },
    // Onscreen it is Active Date as this is what wage employees know it by but it is mapped to Posting Start Date
    { id: 'postingStartDate', label: 'Active Date' },
    { id: 'endDate', label: 'Bid Close' },
];

const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {columns.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            IconComponent={ArrowDropDownIcon}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};
export const PendingJobs = () => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('department');
    const [alertMsg, setAlertMsg] = useState(null);
    const [alertVariant, setAlertVariant] = useState(false);
    const location = useLocation();
    const { makeRequest, authState } = useContext(LoggedInContext);

    const handleRequestSort = (_event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    useEffect(() => {
        let ignore = false;
        const fetchBids = async () => {
            setIsLoading(true);
            if (!ignore) {
                const response = await makeRequest(
                    process.env.REACT_APP_API_ENDPOINT + '/bids/pending'
                );
                if (response.ok) {
                    const responseData = await response.json();
                    const bidRows = responseData.map((bid) => ({
                        bidNumber: bid.id,
                        facility: bid.facility,
                        department: bid.department,
                        classification: bid.classification.classification,
                        noOfOpenings: bid.noOfOpenings,
                        jobGrade: bid.jobGrade ?? 4, //need to remove
                        postingStartDate: new Date(
                            bid.postingStartDate
                        ).toLocaleDateString('en-US', {
                            year: '2-digit',
                            month: '2-digit',
                            day: '2-digit',
                        }),
                        endDate: new Date(bid.endDate).toLocaleDateString(
                            'en-US',
                            {
                                year: '2-digit',
                                month: '2-digit',
                                day: '2-digit',
                            }
                        ),
                        reqNumber: bid.reqNumber,
                    }));
                    bidRows.sort((a, b) => {
                        const departmentA = a.department.toUpperCase(); // ignore upper and lowercase
                        const departmentB = b.department.toUpperCase(); // ignore upper and lowercase
                        if (departmentA < departmentB) {
                            return -1;
                        }
                        if (departmentA > departmentB) {
                            return 1;
                        }
                        return 0;
                    });
                    setRows(bidRows);
                } else {
                    setAlertMsg(
                        errorBannerFromResponse(
                            response,
                            'We are unable to retrieve pending jobs. Please try again later.'
                        ).message
                    );
                    setAlertVariant('error');
                }
            }
            setIsLoading(false);
        };
        fetchBids();

        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [authState, makeRequest]);

    const pendingJobDownload = async () => {
        const response = await makeRequest(
            process.env.REACT_APP_API_ENDPOINT + '/pendingJobsDownload'
        );
        if (response.ok) {
            const filename = response.headers
                .get('content-disposition')
                .split('filename=')[1];
            const pendingJobDownloadResponse = await response.blob();
            const fileURL = window.URL.createObjectURL(
                pendingJobDownloadResponse
            );
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = filename;
            link.target = '_blank';
            link.click();
            window.URL.revokeObjectURL(fileURL);
            link.remove();
        } else {
            setAlertMsg(
                errorBannerFromResponse(
                    response,
                    'We are unable to download the pending jobs list. Please try again later.'
                ).message
            );
            setAlertVariant('error');
        }
    };

    return (
        <div className={styles.parentContainer}>
            <Box className={styles.contentToContainer}>
                {location.state?.message && (
                    <AlertBox
                        variant={location.state.variant}
                        msg={location.state.message}
                    ></AlertBox>
                )}
                {!location.state?.message && alertVariant && (
                    <AlertBox variant={alertVariant} msg={alertMsg}></AlertBox>
                )}
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    <Typography variant="h1" className={styles.heading}>
                        Pending Jobs
                    </Typography>
                </Box>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: '1.5rem',
                    }}
                >
                    Click on the job bid number to make changes to your job
                    posting.
                    <Button
                        variant="primary"
                        disabled={rows.length === 0 || isLoading}
                        startIcon={<img alt=">>" src={DownloadIcon} />}
                        className={styles.buttonDownload}
                        onClick={pendingJobDownload}
                    >
                        Download Pending Job List
                    </Button>
                </Box>
                {isLoading && (
                    <div style={{ width: '70rem' }}>
                        <CircularProgress
                            size="lg"
                            variant="indeterminate"
                            title={'Loading'}
                        />
                    </div>
                )}
                {!isLoading && rows.length === 0 && (
                    <Box minWidth={1100}>
                        <Card>
                            <CardContent>
                                <Grid container spacing={12}>
                                    <Grid item xs={12} sm={8}>
                                        <Typography gutterBottom variant="h4">
                                            There are no pending jobs at this
                                            time.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}
                {!isLoading && rows.length > 0 && (
                    <Paper>
                        <TableContainer>
                            <Table>
                                <EnhancedTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableBody>
                                    {rows
                                        .sort(getComparator(order, orderBy))
                                        .map((row) => {
                                            return (
                                                <TableRow
                                                    hover
                                                    tabIndex={-1}
                                                    key={row.bidNumber}
                                                >
                                                    <TableCell>
                                                        <Link
                                                            to={
                                                                '/edit-job/' +
                                                                row.bidNumber
                                                            }
                                                            state={row}
                                                        >
                                                            {row.bidNumber}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.facility}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.department}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.classification}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.noOfOpenings}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.reqNumber}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.jobGrade}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.postingStartDate}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.endDate}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Box>
        </div>
    );
};
